import * as React from 'react';

import { DocumentType } from '@rematter/frontend-lib-api-client';
import {
  DataGrid,
  DataGridSelection,
  FloatingToolbar,
  FontSemiBold,
  ListScreenLayout,
  Pagination,
} from '@rematter/ui-web';
import { useAtom, useAtomValue } from 'jotai';

import { BillRecord } from '../../_graphql/queryBillList';
import * as state from '../../_state/billList.state';
import {
  DownloadDocumentsButton,
  FetchDocumentsPopover,
  PrintDocumentsButton,
} from '../_shared/FetchDocuments';

import { BillListFilterBar } from './BillListFilterBar';
import { useBillListColumns } from './hooks/useBillListColumns';

import { useMultiRef } from '~/shared/hooks/useMultiRef';

//
// ⚛️ ⚛️ ⚛️ BILL LIST VIEW ⚛️ ⚛️ ⚛️
//
export const BillListView = () => {
  // Atoms
  const [limit = 50, setLimit] = useAtom(state.$limit);
  const [offset, setOffset] = useAtom(state.$offset);
  const { data, loadingState, isFetching, error } = useAtomValue(
    state.$remoteBills,
  );

  const menuRefs = useMultiRef<HTMLButtonElement>();

  // States
  const [billIdsForPrint, setBillIdsForPrint] = React.useState<string[]>();
  const [billIdsForDownload, setBillIdsForDownload] =
    React.useState<string[]>();
  const [selection, setSelection] = React.useState<DataGridSelection>(
    new Set(),
  );

  // Memoized values
  const bills = React.useMemo(() => data?.bills ?? [], [data?.bills]);
  const total = React.useMemo(() => data?.total ?? 0, [data?.total]);
  const columns = useBillListColumns({
    menuRefs,
    onPrintBills: setBillIdsForPrint,
    onDownloadBills: setBillIdsForDownload,
  });
  const availableDocuments = React.useMemo(
    () => [
      {
        label: 'Bill',
        documentType: 'Bill' as DocumentType,
      },
    ],
    [],
  );
  const selectedRowsCount = React.useMemo(
    () => (selection === 'all' ? bills.length : selection.size),
    [selection, bills],
  );
  const selectedBills = React.useMemo(
    () => bills.filter((it) => selection === 'all' || selection.has(it.id)),
    [bills, selection],
  );
  const emptyPanelProps = React.useMemo(
    () => ({
      title: "It's empty here",
      description: 'No bills found for the selected filters',
    }),
    [],
  );

  // Callbacks
  const handlePaginationChange = React.useCallback(
    ({
      limit: newLimit,
      offset: newOffset,
    }: {
      limit: number;
      offset: number;
    }) => {
      setOffset(newOffset);
      setLimit(newLimit);
    },
    [setOffset, setLimit],
  );

  return (
    <ListScreenLayout
      filters={<BillListFilterBar />}
      footer={
        total > limit ? (
          <Pagination
            limit={limit}
            offset={offset}
            total={total}
            onChange={handlePaginationChange}
          />
        ) : undefined
      }
      className="@BillListView"
    >
      <DataGrid<BillRecord>
        selectionMode="multiple"
        columns={columns}
        dataSource={bills}
        groupBy={(bill) => bill.supplier.id}
        fullWidth
        isLoading={loadingState.isInitialLoading || isFetching}
        hasError={!!error}
        errorPanelProps={
          error ? { description: error.message, centered: true } : undefined
        }
        rowKey="id"
        selectedKeys={selection}
        onSelectionChange={setSelection}
        emptyPanelProps={emptyPanelProps}
        className="w-full text-sm"
      />
      <FloatingToolbar visible={selectedRowsCount > 0}>
        <div className="flex items-center gap-2">
          <div className="flex min-w-[135px] items-center justify-end gap-1.5 px-3 text-sm text-neutral/80">
            <FontSemiBold>{selectedRowsCount}</FontSemiBold>
            {selectedRowsCount === 1 ? 'bill' : 'bills'} selected
          </div>
          <PrintDocumentsButton
            originKey="bill-list"
            ids={selectedBills.map((it) => it.id)}
            documents={availableDocuments}
          />
          <DownloadDocumentsButton
            originKey="bill-list"
            compressedFileName="bills"
            ids={selectedBills.map((it) => it.id)}
            documents={availableDocuments}
          />
        </div>
      </FloatingToolbar>
      {billIdsForPrint && (
        <FetchDocumentsPopover
          isOpen
          type="print"
          documents={availableDocuments}
          originKey="bill-list-row"
          triggerRef={
            billIdsForPrint.length === 1
              ? menuRefs(billIdsForPrint[0])
              : menuRefs(
                  `group-${bills.find((b) => billIdsForPrint.includes(b.id))?.supplier.id}`,
                )
          }
          ids={billIdsForPrint}
          onOpenChange={() => setBillIdsForPrint(undefined)}
        />
      )}
      {billIdsForDownload && (
        <FetchDocumentsPopover
          isOpen
          type="download"
          documents={availableDocuments}
          originKey="bill-list-row"
          triggerRef={
            billIdsForDownload.length === 1
              ? menuRefs(billIdsForDownload[0])
              : menuRefs(
                  `group-${bills.find((b) => billIdsForDownload.includes(b.id))?.supplier.id}`,
                )
          }
          ids={billIdsForDownload}
          onOpenChange={() => setBillIdsForDownload(undefined)}
        />
      )}
    </ListScreenLayout>
  );
};
